import get from 'lodash/get';
import { logInfo } from 'client/modules/common/utils/logInfo';
import { request } from 'client/modules/common/utils/request';
import { setToastError } from 'client/modules/third-party-admin/profile-queue/utils';

export interface DealInvestor {
  name: string;
  idCbiEntity: number;
}

export interface FieldDiff {
  field: string;
  extracted: string;
  published: string;
}

// A funding published for this company after the deal was extracted (live check; may be a duplicate).
export interface PotentialDuplicateFunding {
  idFunding: number;
  round: string;
  amount: number | null; // published amount; null when not populated
  fundingDate: string; // YYYY-MM-DD, empty when unknown
}

// An active (unpublished) funding draft for this company that may cover the same deal.
export interface PotentialDuplicateDraft {
  idFundingDraft: string;
  idFunding: number; // >0 when the draft edits an already-published funding, else 0
  round: string;
  amount: number | null; // draft amount; null when not set
  fundingDate: string; // YYYY-MM-DD, empty when unknown
  createdAt: string; // when the draft was started
}

// Set only when this deal's cluster already links to a published funding. `differences` lists the
// fields where the extraction disagrees with that funding (empty => identical, but still flagged).
export interface PublishedFundingRef {
  idFunding: number;
  differences: FieldDiff[];
}

export interface DealExtraction {
  idNewsDeal: number;
  idNews: number;
  idCbiEntity: number;
  companyName: string;
  companyDescription: string;
  companyUrl: string;
  dealType: string;
  round: string;
  roundRaw: string;
  amount: number | null; // USD, in millions; null when not populated (e.g. currency conversion failed)
  amountRaw: number | null; // pre-conversion amount (in amountCurrency); shown only when amount is null
  amountCurrency: string;
  dealDate: string;
  extractedAt: string; // news_deal.created_at -- when the extraction was ingested
  dealStatus: string;
  sentence: string;
  investors: DealInvestor[];
  newsTitle: string;
  newsUrl: string;
  newsLanguage: string;
  isMainDeal: boolean;
  newsSourceId: number;
  publishedFunding: PublishedFundingRef | null; // set only when already linked to a published funding
  potentialDuplicates: PotentialDuplicateFunding[]; // later-published fundings not linked to this deal
  potentialDuplicateDrafts: PotentialDuplicateDraft[]; // active funding drafts that may cover this deal
}

export interface DealCard {
  idNewsDeal: number;
  anchor: DealExtraction;
  memberDeals: DealExtraction[];
}

export interface DealQueueFilters {
  dealType?: number;
  startDate?: string;
  endDate?: string;
  sortByDate?: number;
  groupBy?: number;
  idCbiEntity?: number[];
  amountMin?: number;
  amountMax?: number;
  idSource?: number[];
  dateField?: number;
  companyCategory?: number;
  languages?: number[];
  hidePublished?: boolean;
  primaryDealsOnly?: boolean;
}

export interface DealQueueData {
  dealCards: DealCard[];
  totalCount: number;
  isLoading: boolean;
}

// proto3 JSON omits default/empty fields, so fill safe defaults for rendering.
function normalizeExtraction(raw: Record<string, unknown>): DealExtraction {
  const amount = get(raw, 'amount');
  const amountRaw = get(raw, 'amountRaw');
  return {
    idNewsDeal: (get(raw, 'idNewsDeal', 0) as number) || 0,
    idNews: (get(raw, 'idNews', 0) as number) || 0,
    idCbiEntity: (get(raw, 'idCbiEntity', 0) as number) || 0,
    companyName: (get(raw, 'companyName', '') as string) || '',
    companyDescription: (get(raw, 'companyDescription', '') as string) || '',
    companyUrl: (get(raw, 'companyUrl', '') as string) || '',
    dealType:
      (get(raw, 'dealType', 'DEAL_TYPE_UNSPECIFIED') as string) ||
      'DEAL_TYPE_UNSPECIFIED',
    round: (get(raw, 'round', '') as string) || '',
    roundRaw: (get(raw, 'roundRaw', '') as string) || '',
    amount: amount === undefined || amount === null ? null : (amount as number),
    amountRaw:
      amountRaw === undefined || amountRaw === null
        ? null
        : (amountRaw as number),
    amountCurrency: (get(raw, 'amountCurrency', '') as string) || '',
    dealDate: (get(raw, 'dealDate', '') as string) || '',
    extractedAt: (get(raw, 'extractedAt', '') as string) || '',
    dealStatus: (get(raw, 'dealStatus', '') as string) || '',
    sentence: (get(raw, 'sentence', '') as string) || '',
    investors: (get(raw, 'investors', []) as DealInvestor[]) || [],
    newsTitle: (get(raw, 'newsTitle', '') as string) || '',
    newsUrl: (get(raw, 'newsUrl', '') as string) || '',
    newsLanguage: (get(raw, 'newsLanguage', '') as string) || '',
    isMainDeal: Boolean(get(raw, 'isMainDeal', false)),
    newsSourceId: (get(raw, 'newsSourceId', 0) as number) || 0,
    // proto3 omits unset message fields, so publishedFunding is only present when linked.
    publishedFunding: get(raw, 'publishedFunding')
      ? {
          idFunding: (get(raw, 'publishedFunding.idFunding', 0) as number) || 0,
          differences:
            (get(raw, 'publishedFunding.differences', []) as FieldDiff[]) || [],
        }
      : null,
    potentialDuplicates: (
      (get(raw, 'potentialDuplicates', []) as Array<Record<string, unknown>>) ||
      []
    ).map((dup) => ({
      idFunding: (get(dup, 'idFunding', 0) as number) || 0,
      round: (get(dup, 'round', '') as string) || '',
      // optional proto field: absent (not 0) when the published amount is NULL.
      amount:
        get(dup, 'amount') == null ? null : (get(dup, 'amount') as number),
      fundingDate: (get(dup, 'fundingDate', '') as string) || '',
    })),
    potentialDuplicateDrafts: (
      (get(raw, 'potentialDuplicateDrafts', []) as Array<
        Record<string, unknown>
      >) || []
    ).map((draft) => ({
      idFundingDraft: (get(draft, 'idFundingDraft', '') as string) || '',
      idFunding: (get(draft, 'idFunding', 0) as number) || 0,
      round: (get(draft, 'round', '') as string) || '',
      // optional proto field: absent (not 0) when the draft has no amount.
      amount:
        get(draft, 'amount') == null ? null : (get(draft, 'amount') as number),
      fundingDate: (get(draft, 'fundingDate', '') as string) || '',
      createdAt: (get(draft, 'createdAt', '') as string) || '',
    })),
  };
}

export function mapDealQueueResponse(
  body: Record<string, unknown>
): DealQueueData {
  const rawCards = get(body, 'dealCards', []) as Array<Record<string, unknown>>;
  const dealCards: DealCard[] = rawCards.map((card) => ({
    idNewsDeal: (get(card, 'idNewsDeal', 0) as number) || 0,
    anchor: normalizeExtraction(
      get(card, 'anchor', {}) as Record<string, unknown>
    ),
    memberDeals: (
      get(card, 'memberDeals', []) as Array<Record<string, unknown>>
    ).map(normalizeExtraction),
  }));

  return {
    dealCards,
    totalCount: (get(body, 'totalCount', 0) as number) || 0,
    isLoading: false,
  };
}

export function getDealQueue(
  filters: DealQueueFilters,
  limit = 25
): Promise<DealQueueData> {
  return request({
    url: '/service/newsqueueservice/GetDealReviewQueue',
    body: { filters, limit },
  })
    .then(({ body }) => mapDealQueueResponse(body))
    .catch((error) => {
      setToastError('Error Fetching Deal Queue');
      logInfo({
        error,
        message: 'Deal Queue: Failed To Fetch Queue',
        method: 'getDealQueue',
      });
      throw error;
    });
}
